import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getFAQs, createFAQ, updateFAQ, deleteFAQ, getFaqQuestions, updateFaqQuestion, deleteFaqQuestion, answerFaqQuestion } from "../../lib/api";
import { Plus, Save, Trash2, HelpCircle, MessageSquare, CheckCircle, XCircle, Mail, User, Pencil, X, Send, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

export default function AdminFaq() {
  const [faqs, setFaqs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("faqs");
  const [qFilter, setQFilter] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "" });
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [answeringId, setAnsweringId] = useState(null);
  const [answeredId, setAnsweredId] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [faqsData, questionsData] = await Promise.all([
          getFAQs(),
          getFaqQuestions(),
        ]);
        setFaqs(faqsData || []);
        setQuestions(questionsData || []);
      } catch (err) {
        console.error(err);
        toast.error("Erro ao carregar FAQ");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleCreate() {
    if (!form.question.trim() || !form.answer.trim()) return toast.error("Preencha pergunta e resposta");
    try {
      const created = await createFAQ({ question: form.question.trim(), answer: form.answer.trim(), sort_order: faqs.length });
      setFaqs((prev) => [...prev, created]);
      setForm({ question: "", answer: "" });
      setShowAddForm(false);
      toast.success("FAQ adicionada");
    } catch (err) {
      toast.error("Erro ao adicionar FAQ");
    }
  }

  async function handleUpdate(id) {
    setSaving(true);
    try {
      await updateFAQ(id, { question: form.question, answer: form.answer });
      setFaqs((prev) => prev.map((f) => (f.id === id ? { ...f, question: form.question, answer: form.answer } : f)));
      setEditingFaq(null);
      toast.success("FAQ atualizada");
    } catch (err) {
      toast.error("Erro ao atualizar");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteFAQ(id);
      setFaqs((prev) => prev.filter((f) => f.id !== id));
      if (editingFaq === id) setEditingFaq(null);
      setFaqToDelete(null);
      toast.success("FAQ removida");
    } catch (err) {
      toast.error("Erro ao remover");
    }
  }

  function startEdit(faq) {
    setEditingFaq(faq.id);
    setForm({ question: faq.question, answer: faq.answer });
  }

  function cancelEdit() {
    setEditingFaq(null);
    setForm({ question: "", answer: "" });
  }

  async function handleAnswerQuestion(q) {
    if (!q.answer?.trim()) return toast.error("Escreva uma resposta antes de enviar");
    setAnsweringId(q.id);
    try {
      const updated = await answerFaqQuestion({ id: q.id, answer: q.answer });
      setQuestions((prev) => prev.map((qi) => (qi.id === q.id ? { ...qi, ...updated } : qi)));
      setAnsweredId(q.id);
      toast.success("Resposta enviada por email para " + q.name);
      setTimeout(() => setAnsweredId(null), 3000);
    } catch (err) {
      toast.error("Erro ao responder");
    } finally {
      setAnsweringId(null);
    }
  }

  async function handleToggleQuestionStatus(id, current) {
    const next = current === "published" ? "pending" : "published";
    try {
      await updateFaqQuestion(id, { status: next });
      setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, status: next } : q)));
      toast.success(next === "published" ? "Publicada no site" : "Arquivada");
    } catch (err) {
      toast.error("Erro ao atualizar");
    }
  }

  async function handleDeleteQuestion(id) {
    try {
      await deleteFaqQuestion(id);
      setQuestions((prev) => prev.filter((q) => q.id !== id));
      setQuestionToDelete(null);
      toast.success("Pergunta removida");
    } catch (err) {
      toast.error("Erro ao remover");
    }
  }

  const pendingCount = questions.filter((q) => q.status === "pending").length;
  const answeredCount = questions.filter((q) => q.status === "answered").length;
  const publishedCount = questions.filter((q) => q.status === "published").length;

  const filteredQuestions = qFilter === "all"
    ? questions
    : questions.filter((q) => q.status === qFilter);

  if (loading) return <div className="py-12 text-center text-warmgray text-sm">Carregando...</div>;

  return (
    <div data-testid="admin-faq-tab">
      {/* Tab nav */}
      <div className="flex items-center border-b border-fendi/30 mb-8">
        <button
          onClick={() => setTab("faqs")}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors ${tab === "faqs" ? "text-softblack border-b border-champagne" : "text-warmgray hover:text-softblack"}`}
        >
          <HelpCircle size={14} strokeWidth={1.5} /> FAQs <span className="text-warmgray text-[10px]">({faqs.length})</span>
        </button>
        <button
          onClick={() => setTab("questions")}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors ${tab === "questions" ? "text-softblack border-b border-champagne" : "text-warmgray hover:text-softblack"}`}
        >
          <MessageSquare size={14} strokeWidth={1.5} /> Recebidas
          {pendingCount > 0 && (
            <span className="inline-flex items-center justify-center w-4 h-4 text-[8px] font-bold bg-champagne text-softblack rounded-full">{pendingCount}</span>
          )}
        </button>
      </div>

      {/* === FAQs TAB === */}
      {tab === "faqs" && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-serif text-3xl text-softblack">FAQ</h2>
              <p className="text-warmgray text-sm font-light mt-1">Perguntas e respostas frequentes exibidas no site.</p>
            </div>
            <button onClick={() => { setShowAddForm(!showAddForm); cancelEdit(); }} className="btn-primary !px-6 !py-3 !text-[11px] w-full sm:w-auto flex items-center justify-center">
              <Plus size={16} strokeWidth={1.5} className="mr-2" /> Nova FAQ
            </button>
          </div>

          {showAddForm && (
            <div className="bg-white border border-fendi p-8 mb-10 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="flex items-center justify-between mb-6 border-b border-fendi/30 pb-4">
                <p className="eyebrow !text-xs tracking-widest text-softblack/60">Nova Pergunta</p>
                <button onClick={() => setShowAddForm(false)} className="text-warmgray hover:text-softblack transition-colors"><X size={20} /></button>
              </div>
              <div className="space-y-6">
                <label className="block">
                  <span className="eyebrow mb-3 block">Pergunta</span>
                  <input className="editorial-field w-full" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Ex: Como funciona o processo?" />
                </label>
                <label className="block">
                  <span className="eyebrow mb-3 block">Resposta</span>
                  <textarea className="editorial-field w-full" rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Ex: O processo começa com uma consultoria gratuita..." />
                </label>
                <div className="flex justify-end pt-2">
                  <button onClick={handleCreate} className="btn-primary !px-10"><Plus size={14} className="mr-2" /> Adicionar FAQ</button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {faqs.length === 0 && !showAddForm && (
              <div className="text-center text-warmgray font-light py-20 bg-fendi/5 border border-dashed border-fendi">Nenhuma FAQ cadastrada.</div>
            )}

            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white border border-fendi hover:shadow-sm transition-all duration-300">
                {editingFaq === faq.id ? (
                  <div className="p-8 bg-champagne/10 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between mb-6 border-b border-fendi/20 pb-3">
                      <p className="eyebrow !text-[10px] text-softblack/50">Editando FAQ</p>
                      <button onClick={cancelEdit} className="text-warmgray hover:text-softblack"><X size={16} /></button>
                    </div>
                    <div className="space-y-6">
                      <label className="block">
                        <span className="eyebrow mb-2 block">Pergunta</span>
                        <input className="editorial-field w-full" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
                      </label>
                      <label className="block">
                        <span className="eyebrow mb-2 block">Resposta</span>
                        <textarea className="editorial-field w-full" rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
                      </label>
                      <div className="flex items-center justify-between pt-2">
                        <button onClick={() => setFaqToDelete(faq.id)} className="text-xs text-warmgray hover:text-destructive transition-colors flex items-center gap-1">
                          <Trash2 size={12} /> Excluir
                        </button>
                        <button onClick={() => handleUpdate(faq.id)} disabled={saving} className="btn-primary !px-8 !py-2"><Save size={12} className="mr-2" /> Salvar</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 md:p-8 flex items-start justify-between gap-6 group">
                    <div className="flex gap-4 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-champagne/10 border border-champagne/20 flex items-center justify-center shrink-0 mt-0.5">
                        <HelpCircle size={16} className="text-champagne" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-softblack">{faq.question}</p>
                        <p className="text-sm text-warmgray font-light mt-2 leading-relaxed line-clamp-2">{faq.answer}</p>
                      </div>
                    </div>
                    <div className="flex gap-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(faq)} className="text-warmgray hover:text-softblack transition-colors" title="Editar"><Pencil size={14} strokeWidth={1.5} /></button>
                      <button onClick={() => setFaqToDelete(faq.id)} className="text-warmgray hover:text-destructive transition-colors" title="Excluir"><Trash2 size={14} strokeWidth={1.5} /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === QUESTIONS TAB === */}
      {tab === "questions" && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-serif text-3xl text-softblack">Perguntas Recebidas</h2>
              <p className="text-warmgray text-sm font-light mt-1">Responda e publique as perguntas dos visitantes.</p>
            </div>
          </div>

          {/* Status filter pills */}
          <div className="flex gap-2 mb-8 flex-wrap">
            {[
              { key: "all", label: "Todas", count: questions.length },
              { key: "pending", label: "Pendentes", count: pendingCount },
              { key: "answered", label: "Respondidas", count: answeredCount },
              { key: "published", label: "Publicadas", count: publishedCount },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setQFilter(f.key)}
                className={`text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${qFilter === f.key ? "bg-softblack text-offwhite border-softblack" : "bg-white text-warmgray border-fendi/30 hover:border-fendi/60"}`}
              >
                {f.label} <span className="opacity-60">({f.count})</span>
              </button>
            ))}
          </div>

          <div className="space-y-6">
            {filteredQuestions.length === 0 && (
              <div className="text-center text-warmgray font-light py-20 bg-fendi/5 border border-dashed border-fendi">
                {qFilter === "all" ? "Nenhuma pergunta recebida." : `Nenhuma pergunta ${qFilter}.`}
              </div>
            )}

            {filteredQuestions.map((q) => {
              const isSending = answeringId === q.id;
              const isSent = answeredId === q.id;

              return (
                <div key={q.id} className={`bg-white border overflow-hidden transition-all duration-300 ${isSent ? "border-champagne shadow-md" : "border-fendi"}`}>
                  <div className="p-6 md:p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-fendi/10 border border-fendi/20 flex items-center justify-center shrink-0">
                          <User size={15} className="text-warmgray" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-sm text-softblack font-medium">{q.name}</p>
                          <a href={`mailto:${q.email}`} className="text-xs text-champagne hover:text-softblack transition-colors inline-flex items-center gap-1 mt-0.5">
                            <Mail size={10} /> {q.email}
                          </a>
                        </div>
                      </div>
                      <span className={`text-[9px] uppercase tracking-wider px-3 py-1 border shrink-0 ${q.status === 'published' ? 'border-champagne text-champagne bg-champagne/5' : q.status === 'answered' ? 'border-fendi/40 text-warmgray bg-fendi/5' : 'border-fendi/20 text-warmgray bg-fendi/5'}`}>
                        {q.status === 'published' ? 'Publicado' : q.status === 'answered' ? 'Respondido' : 'Pendente'}
                      </span>
                    </div>

                    {/* Question quote */}
                    <div className="bg-fendi/5 border-l-2 border-champagne/40 pl-5 py-4 pr-4 mb-6">
                      <p className="text-sm text-softblack leading-relaxed italic">&ldquo;{q.question}&rdquo;</p>
                    </div>

                    {/* Answer form */}
                    <div className="space-y-4">
                      <label className="block">
                        <span className="eyebrow mb-2 block text-[10px] text-warmgray">Sua Resposta</span>
                        <textarea
                          className="editorial-field w-full"
                          rows={3}
                          value={q.answer || ""}
                          onChange={(e) => setQuestions((prev) => prev.map((qi) => qi.id === q.id ? { ...qi, answer: e.target.value } : qi))}
                          placeholder="Digite sua resposta..."
                          disabled={isSending}
                        />
                      </label>

                      {isSent && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-champagne/10 border border-champagne/30 text-xs text-softblack">
                          <CheckCircle size={14} className="text-champagne" />
                          Resposta enviada para {q.email}
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleQuestionStatus(q.id, q.status)}
                            className={`text-xs flex items-center gap-1 px-3 py-1.5 border transition-colors ${q.status === 'published' ? 'border-champagne text-champagne' : 'border-fendi text-warmgray hover:text-softblack'}`}
                          >
                            {q.status === 'published' ? <XCircle size={11} /> : <CheckCircle size={11} />}
                            {q.status === 'published' ? 'Arquivar' : 'Publicar no site'}
                          </button>
                          <button onClick={() => setQuestionToDelete(q.id)} className="text-xs text-warmgray hover:text-destructive transition-colors flex items-center gap-1 px-3 py-1.5 border border-fendi">
                            <Trash2 size={11} /> Remover
                          </button>
                        </div>
                        <button
                          onClick={() => handleAnswerQuestion(q)}
                          disabled={isSending || !q.answer?.trim()}
                          className={`btn-primary !px-8 !py-2 flex items-center gap-2 ${isSent ? '!bg-champagne !text-softblack' : ''}`}
                        >
                          {isSending ? (
                            <><Loader2 size={12} className="animate-spin" /> Enviando...</>
                          ) : isSent ? (
                            <><CheckCircle size={12} /> Enviado</>
                          ) : (
                            <><Send size={12} /> {q.answer?.trim() ? "Responder e Enviar Email" : "Responder"}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Delete FAQ confirmation */}
      <AlertDialog open={!!faqToDelete} onOpenChange={() => setFaqToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Excluir FAQ?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente esta pergunta frequente do site. Você não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(faqToDelete)} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete question confirmation */}
      <AlertDialog open={!!questionToDelete} onOpenChange={() => setQuestionToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Excluir Pergunta?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente esta pergunta e sua resposta. Você não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteQuestion(questionToDelete)} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
